/**
 * Líneas de negocio del dashboard y a dónde entra cada usuario (feature 108, D4).
 *
 * TypeScript puro (capa domain). La MISMA regla la consumen `UserRoleService`,
 * `gestor.guard`, el host `/dashboard`, el selector de línea y el sidebar: con dos
 * copias ya divergieron (el selector ofrecía Envíos a un usuario solo de Cards, que
 * banca rechaza con 403 desde la feature 98).
 *
 * Merge de `dev` (2026-10-08): la Pasarela de Pagos (feature 60 de dev,
 * `specs/dashboard_pasarela_pagos/`) suma la línea `payments` con su vista
 * `/e-commerce/dashboard`. PAYMENTS sigue SIN dar la línea SPEI (la razón de R29).
 */
import { servicioDeGestor } from './gestor-servicio';

/** Línea de negocio derivada de los gestores del usuario. */
export type BusinessLine = 'spei' | 'tpv' | 'cards' | 'payments';

/** Vista del host `/dashboard`: Todo (global), Envíos (SPEI), TPV o Pasarela de Pagos. */
export type DashboardView = 'all' | 'transactions' | 'tpv' | 'payments';

/** Ruta del dashboard de la Pasarela de Pagos (vive bajo su módulo, como TPV y Cards). */
export const PAYMENTS_DASHBOARD_ROUTE = '/e-commerce/dashboard';

/** Entrada de dashboard de un usuario. */
export type DashboardEntryRoute =
  | '/dashboard'
  | '/tpv/dashboard'
  | '/cards/dashboard'
  | typeof PAYMENTS_DASHBOARD_ROUTE
  | '/products';

/** Lo único que esta regla lee de un gestor de la sesión. */
export interface GestorLike {
  service?: unknown;
}

/** Opciones de la regla de líneas. */
export interface BusinessLinesOptions {
  /**
   * E-commerce encendido en el ambiente (kill-switch del deploy). Sin él, PAYMENTS no
   * da línea: su dashboard vive bajo `/e-commerce`. Ausente = apagado (el dominio no
   * lee el ambiente; lo pasa quien lo conoce).
   */
  readonly paymentsEnabled?: boolean;
}

/**
 * R29: `TPV` ⇒ tpv; `CARDS` ⇒ cards; `SPEI` o vacío (gestor legado sin `service`)
 * ⇒ spei; `PAYMENTS` ⇒ payments SOLO con E-commerce encendido (y NUNCA spei: antes
 * contaba como SPEI y llevaba a un Envíos que respondía 403); cualquier otro valor ⇒
 * NINGUNA línea. Sin duplicados, en orden de aparición, con `payments` al final para
 * que las líneas que ya existían conserven su orden.
 */
export function businessLinesFromGestores(
  gestores: ReadonlyArray<GestorLike | null | undefined>,
  options: BusinessLinesOptions = {},
): BusinessLine[] {
  const lines = new Set<BusinessLine>();
  let hasPayments = false;
  for (const gestor of gestores) {
    const service = servicioDeGestor(gestor?.service);
    if (service === 'TPV') lines.add('tpv');
    else if (service === 'CARDS') lines.add('cards');
    else if (service === '' || service === 'SPEI') lines.add('spei');
    else if (service === 'PAYMENTS') hasPayments = true;
  }
  if (hasPayments && options.paymentsEnabled === true) lines.add('payments');
  return [...lines];
}

/** R35: Todo con 2+ líneas, Envíos con SPEI, TPV con TPV, Pasarela con payments. */
export function allowedDashboardViews(lines: ReadonlyArray<BusinessLine>): ReadonlySet<DashboardView> {
  const views = new Set<DashboardView>();
  if (new Set(lines).size >= 2) views.add('all');
  if (lines.includes('spei')) views.add('transactions');
  if (lines.includes('tpv')) views.add('tpv');
  if (lines.includes('payments')) views.add('payments');
  return views;
}

/**
 * R35: la vista por líneas del host. 2+ ⇒ Todo; TPV ⇒ TPV; solo payments ⇒ Pasarela;
 * si no, Envíos (el guard del host ya garantiza que, si no hay 2+, hay SPEI).
 */
export function defaultDashboardView(lines: ReadonlyArray<BusinessLine>): DashboardView {
  const unique = new Set(lines);
  if (unique.size >= 2) return 'all';
  if (unique.has('tpv')) return 'tpv';
  if (unique.has('payments')) return 'payments';
  return 'transactions';
}

/**
 * R34: UNA sola entrada de dashboard. 2+ líneas o SPEI ⇒ `/dashboard`; solo TPV ⇒
 * `/tpv/dashboard`; solo Tarjetas ⇒ `/cards/dashboard`; solo Pasarela ⇒
 * `/e-commerce/dashboard`; ninguna ⇒ `/products` (nunca `/login` para una sesión válida).
 */
export function dashboardEntryRoute(lines: ReadonlyArray<BusinessLine>): DashboardEntryRoute {
  const unique = new Set(lines);
  if (unique.size >= 2 || unique.has('spei')) return '/dashboard';
  if (unique.has('tpv')) return '/tpv/dashboard';
  if (unique.has('cards')) return '/cards/dashboard';
  if (unique.has('payments')) return PAYMENTS_DASHBOARD_ROUTE;
  return '/products';
}
